import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BadgePercent, Zap, BookOpen, ShieldCheck } from "lucide-react";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ProductCard } from "../components/ProductCard";
import { toast } from "sonner";

const BENEFITS = [
  { icon: BadgePercent, title: "5% Member Pricing", desc: "Automatic 5% off every order when you're signed in. No codes, no minimums." },
  { icon: Zap, title: "Early Access", desc: "Shop new gear drops before they open to the public." },
  { icon: BookOpen, title: "Field Guide First", desc: "New preparedness guides and checklists land in your inbox first." },
];

export default function Club() {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [joining, setJoining] = useState(false);
  const [earlyAccess, setEarlyAccess] = useState([]);
  const [memberCount, setMemberCount] = useState(null);

  useEffect(() => {
    api.get("/club/early-access").then(({ data }) => setEarlyAccess(data)).catch(() => {});
    api.get("/club/stats").then(({ data }) => setMemberCount(data.member_count)).catch(() => {});
  }, []);

  const join = async () => {
    if (!user) { navigate("/login"); return; }
    setJoining(true);
    try {
      await api.post("/club/join");
      await checkAuth();
      toast.success("Welcome to the Preparedness Club — 5% member pricing is now active!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not join right now");
    } finally { setJoining(false); }
  };

  const isMember = user?.club_member;

  return (
    <div>
      <section className="bg-[#121212] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-20">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#8FA84E] font-mono">Free Membership</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl uppercase font-black tracking-tighter mt-3 leading-none">Shodan<br />Preparedness Club</h1>
          <p className="text-white/60 mt-4 max-w-lg">Member pricing on every order, early access to new gear, and first look at every Field Guide — free for account holders.</p>
          {memberCount !== null && memberCount > 0 && (
            <p data-testid="club-member-count" className="mt-5 inline-flex items-center gap-2 text-[#8FA84E] text-sm font-bold uppercase tracking-widest">
              <span className="relative flex h-2.5 w-2.5"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#8FA84E] opacity-60"></span><span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#8FA84E]"></span></span>
              Join {memberCount.toLocaleString()}+ prepared members
            </p>
          )}
          {isMember ? (
            <div data-testid="club-member-badge" className="inline-flex items-center gap-2 mt-8 border-2 border-[#8FA84E] text-[#8FA84E] px-6 py-3.5 text-sm font-bold uppercase tracking-widest">
              <ShieldCheck size={18} /> You're a Member — 5% pricing active
            </div>
          ) : (
            <button data-testid="club-join-btn" onClick={join} disabled={joining}
              className="mt-8 bg-[#4A5D23] text-white px-10 py-4 text-sm font-bold uppercase tracking-widest hover:bg-[#8FA84E] transition-colors duration-150 disabled:opacity-50">
              {joining ? "Joining…" : user ? "Join Free" : "Sign In to Join Free"}
            </button>
          )}
          {!user && <p className="text-white/40 text-xs mt-3">No account yet? <Link to="/register" className="text-[#8FA84E] font-bold hover:underline">Create one free</Link> — then join in one tap.</p>}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 md:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {BENEFITS.map((b) => (
            <div key={b.title} className="border border-black/10 p-8">
              <b.icon size={26} className="text-[#4A5D23]" />
              <h2 className="font-heading font-bold uppercase tracking-tight text-lg mt-4">{b.title}</h2>
              <p className="text-sm text-black/60 mt-2">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {earlyAccess.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 md:px-8 pb-20">
          <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-[#4A5D23] font-mono">Members See It First</p>
              <h2 className="font-heading text-2xl md:text-3xl uppercase font-black tracking-tight mt-1">Early Access Drops</h2>
            </div>
            {!isMember && <p className="text-xs text-black/50">Join the club to shop these before everyone else.</p>}
          </div>
          <div data-testid="early-access-grid" className={`grid grid-cols-2 md:grid-cols-4 gap-4 ${!isMember ? "opacity-90" : ""}`}>
            {earlyAccess.map((p) => (
              <div key={p.slug} className="relative">
                {!isMember && <div className="absolute inset-0 z-10 backdrop-blur-[3px] bg-white/40 flex items-center justify-center">
                  <button data-testid={`early-access-locked-${p.slug}`} onClick={join} className="bg-[#121212] text-white px-4 py-2.5 text-[10px] font-bold uppercase tracking-widest hover:bg-[#4A5D23] transition-colors duration-150">Members Only — Join Free</button>
                </div>}
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
